import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
    Card, 
    DatePicker, 
    Upload, 
    Button, 
    Table, 
    Typography,
    message,
    Spin,
    Space,
    Tabs,
    Row,
    Col,
    Statistic,
    Alert,
    Progress,
    Tag,
    Tooltip,
    Divider,
    Select
} from 'antd';
import { 
    UploadOutlined, 
    LineChartOutlined, 
    CarOutlined, 
    RiseOutlined,
    CalendarOutlined,
    BarChartOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    DatabaseOutlined,
    InfoCircleOutlined,
    FileTextOutlined,
    PieChartOutlined,
    ClockCircleOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import { OSRReport } from '../model/reports';
import dayjs from '../dayjs';
import { buildGtfsZip } from '../libs/exportLib';
import { values } from 'lodash/object';
import { routeModelData, stopModelData, scheduleModelData, driverShiftModelData, vehicleShiftModelData, shiftBatModelData, deadrunModelData } from '../services/ModelService';
import { keyBy } from 'lodash/collection';
import { BusRoute } from '../model/busRoute';
import { useAppContext } from '../libs/contextLib';
import OTRDateRangePicker from '../features/Reportable/components/OTRDateRangePicker';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Helper function to get color based on value
const getValueColor = (value, type = 'default') => {
    if (type === 'percentage') {
        if (value >= 90) return '#3f8600';
        if (value >= 70) return '#faad14';
        return '#cf1322';
    }
    if (type === 'change') {
        if (value > 0) return '#3f8600';
        if (value < 0) return '#cf1322';
        return '#8c8c8c';
    }
    return '#1890ff';
};

// Helper function to format large numbers
const formatNumber = (num) => {
    return num.toLocaleString();
};

// Helper function to convert data to CSV
const convertToCSV = (data, columns) => {
    // Create header row from column titles
    const header = columns.map(col => `"${col.title}"`).join(',');
    
    // Create data rows
    const rows = data.map(item => {
        return columns.map(col => {
            let value = item[col.dataIndex];
            
            // Handle undefined or null values
            if (value === undefined || value === null) {
                return '';
            }
            
            // Convert to string and escape quotes
            return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',');
    }).join('\n');
    
    return `${header}\n${rows}`;
};

// Helper function to download CSV
const downloadCSV = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};



export default function OSRReportComponent() {
    // Initialize dateRange with last 6 months (from 1st of the month, 5 months ago, to end of current month)
    const [dateRange, setDateRange] = useState(() => {
        const endDate = dayjs().endOf('month');
        const startDate = endDate.clone().subtract(5, 'month').startOf('month');
        return [startDate, endDate];
    });
    const [gtfsFile, setGtfsFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [osrReport, setOSRReport] = useState(null);
    const [activeTab, setActiveTab] = useState('1');
    const [selectedContractIds, setSelectedContractIds] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const { operator } = useAppContext();

    // Memoize filtered months calculation since it's used in multiple render functions
    const filteredMonths = useMemo(() => {
        if (!report?.monthlyStats) return [];
        return report.monthlyStats.filter(month => {
            const monthDate = dayjs(month.month + '-01');
            return monthDate.isSameOrAfter(dateRange[0], 'month') && monthDate.isSameOrBefore(dateRange[1], 'month');
        });
    }, [report?.monthlyStats, dateRange]);

    // Memoize filtered route data for contract filtering
    const filteredRouteData = useMemo(() => {
        if (!report?.routeSummary) return [];
        if (selectedContractIds.length === 0) return report.routeSummary;
        return report.routeSummary.filter(route => 
            selectedContractIds.includes(route.networkId)
        );
    }, [report?.routeSummary, selectedContractIds]);

    // Memoize filtered route hours data
    const filteredRouteHoursData = useMemo(() => {
        if (!report?.routeHoursSummary) return [];
        if (selectedContractIds.length === 0) return report.routeHoursSummary;
        return report.routeHoursSummary.filter(route => 
            selectedContractIds.includes(route.networkId)
        );
    }, [report?.routeHoursSummary, selectedContractIds]);

    // Add useEffect to automatically load current GTFS
    useEffect(() => {
        const loadInitialData = async () => {
            // Trigger current GTFS load
            await handleUseCurrentGTFS();
        };

        loadInitialData();
    }, []); // Empty dependency array means this runs once on mount

    const handleFileChange = async (info) => {
        const file = info.file;
        if (!file) {
            setGtfsFile(null);
            return;
        }

        if (!file.name.endsWith('.zip')) {
            message.error('Please upload a valid GTFS zip file');
            setGtfsFile(null);
            return;
        }

        setLoading(true);
        try {
            const report = new OSRReport({
                parameters: {
                    gtfsFile: file
                }
            });
            
            const range = await report.gtfsProcessor.loadGTFSFile(file);
            if (range) {
                const start = dayjs(range.start);
                const end = dayjs(range.end);
                setDateRange([start, end]);
            }
            
            setOSRReport(report);
            setGtfsFile(file);
            message.success('GTFS file loaded successfully');

            // Auto-generate report
            report.parameters.dateRange = {
                start: range.start,
                end: range.end
            };
            const reportData = await report.generateReport();
            setReport(reportData);
            message.success('Report generated successfully');
        } catch (err) {
            console.error('Error loading GTFS:', err);
            message.error('Error loading GTFS file: ' + err.message);
            setGtfsFile(null);
        } finally {
            setLoading(false);
        }
    };

    const handleUseCurrentGTFS = async () => {
        if (!operator) {
            message.error('No operator found. Please make sure you are logged in and have selected an operator.');
            return;
        }

        // Set loading state immediately in a microtask
        await Promise.resolve();
        setLoading(true);
        message.loading('Loading reporting data...', 0);

        // Add a small delay to ensure UI updates
        await new Promise(resolve => setTimeout(resolve, 0));

        try {
            const userId = operator.operatorKey;
            console.time('Total GTFS Generation');
            
            // Fetch all data in parallel
            console.time('Parallel Data Fetch');
            const [
                schedules,
                stops,
                routes,
                shiftBats,
                driverShifts,
                vehicleShifts,
                deadruns
            ] = await Promise.all([
                scheduleModelData.getAll().then(values),
                stopModelData.getAll(userId).then(values),
                routeModelData.getAll().then(values),
                shiftBatModelData.getAll({asArray: true}),
                driverShiftModelData.getAll(),
                vehicleShiftModelData.getAll(),
                deadrunModelData.getAll()
            ]);
            console.timeEnd('Parallel Data Fetch');

            // Process the data
            console.time('Data Processing');
            const stopsById = keyBy(stops.filter(s => (!s.stopType || s.stopType === 'bus')), 'stopId');
            const processedRoutes = routes
                .filter(r => r.published === 1)
                .map(r => new BusRoute(r));
            console.timeEnd('Data Processing');

            // Build GTFS
            console.time('GTFS Build');
            const zip = await buildGtfsZip(
                operator, 
                processedRoutes, 
                schedules, 
                stopsById,
                driverShifts,
                vehicleShifts,
                shiftBats,
                deadruns,
                {exts: {tfnsw: true}, excludeInvalid: false}
            );
            console.timeEnd('GTFS Build');

            // Create file and load into processor
            console.time('File Processing');
            const file = new File([zip.toBuffer()], 'current_gtfs.zip', { type: 'application/zip' });
            const report = new OSRReport({
                parameters: {
                    gtfsFile: file
                }
            });
            
            const range = await report.gtfsProcessor.loadGTFSFile(file);
            if (range) {
                const start = dayjs(range.start);
                const end = dayjs(range.end);
                setDateRange([start, end]);
            }
            console.timeEnd('File Processing');
            
            setOSRReport(report);
            setGtfsFile(file);
            message.destroy();
            message.success('Current GTFS data loaded successfully');

            // Auto-generate report
            report.parameters.dateRange = {
                start: range.start,
                end: range.end
            };
            const reportData = await report.generateReport();
            setReport(reportData);
            message.success('Report generated successfully');
        } catch (err) {
            console.error('Error loading current GTFS:', err);
            message.destroy();
            message.error('Error loading current GTFS data: ' + err.message);
            setGtfsFile(null);
        } finally {
            console.timeEnd('Total GTFS Generation');
            setLoading(false);
        }
    };

    const handleGenerateReport = async () => {
        if (!osrReport || !dateRange) {
            message.error('Please fill in all required fields');
            return;
        }

        setLoading(true);
        message.loading('Generating report...', 0);

        try {
            osrReport.parameters.dateRange = {
                start: dateRange[0].format('YYYY-MM-DD'),
                end: dateRange[1].format('YYYY-MM-DD')
            };
            
            const reportData = await osrReport.generateReport();
            message.destroy();
            message.success('Report generated successfully');
            setReport(reportData);
        } catch (err) {
            console.error('Error generating report:', err);
            message.destroy();
            message.error(err.message || 'Error generating report');
        } finally {
            setLoading(false);
        }
    };

    // Memoize table columns since they're expensive to generate
    const routeKilometersColumns = useMemo(() => [
        {
            title: 'Route',
            dataIndex: 'routeNumber',
            key: 'routeNumber',
            width: 100,
            fixed: 'left',
            render: (text, record) => (
                <Tooltip title={`Network ID: ${record.networkId || 'N/A'}`}>
                    <Text strong>{text}</Text>
                </Tooltip>
            )
        },
        ...filteredMonths.map(month => ({
            title: month.month,
            dataIndex: 'monthlyKilometers',
            key: month.month,
            width: 120,
            render: (monthlyData, record) => {
                const monthData = monthlyData.find(m => m.month === month.month);
                if (!monthData) return '-';
                return (
                    <Tooltip title={`${monthData.serviceCount} services`}>
                        <Text>{formatNumber(Math.round(monthData.kilometers))}</Text>
                    </Tooltip>
                );
            },
            sorter: (a, b) => {
                const aData = a.monthlyKilometers.find(m => m.month === month.month);
                const bData = b.monthlyKilometers.find(m => m.month === month.month);
                return (aData?.kilometers || 0) - (bData?.kilometers || 0);
            }
        })),
        {
            title: 'Total',
            key: 'total',
            width: 120,
            fixed: 'right',
            render: (_, record) => {
                const total = record.monthlyKilometers.reduce((sum, m) => sum + m.kilometers, 0);
                return (
                    <Tooltip title={`${record.totalServices} total services`}>
                        <Text strong>{formatNumber(Math.round(total))}</Text>
                    </Tooltip>
                );
            },
            sorter: (a, b) => {
                const aTotal = a.monthlyKilometers.reduce((sum, m) => sum + m.kilometers, 0);
                const bTotal = b.monthlyKilometers.reduce((sum, m) => sum + m.kilometers, 0);
                return aTotal - bTotal;
            }
        }
    ], [filteredMonths]);

    const routeHoursColumns = useMemo(() => [
        {
            title: 'Route',
            dataIndex: 'routeNumber',
            key: 'routeNumber',
            width: 100,
            fixed: 'left',
            render: (text, record) => (
                <Tooltip title={`Network ID: ${record.networkId || 'N/A'}`}>
                    <Text strong>{text}</Text>
                </Tooltip>
            )
        },
        ...filteredMonths.map(month => ({
            title: month.month,
            dataIndex: 'monthlyHours',
            key: month.month,
            width: 120,
            render: (monthlyData, record) => {
                const monthData = monthlyData.find(m => m.month === month.month);
                if (!monthData) return '-';
                return (
                    <Tooltip title={`${monthData.serviceCount} services`}>
                        <Text>{monthData.hours.toFixed(1)}</Text>
                    </Tooltip>
                );
            },
            sorter: (a, b) => {
                const aData = a.monthlyHours.find(m => m.month === month.month);
                const bData = b.monthlyHours.find(m => m.month === month.month);
                return (aData?.hours || 0) - (bData?.hours || 0);
            }
        })),
        {
            title: 'Total',
            key: 'total',
            width: 120,
            fixed: 'right',
            render: (_, record) => {
                const total = record.monthlyHours.reduce((sum, m) => sum + m.hours, 0);
                return (
                    <Tooltip title={`${record.totalServices} total services`}>
                        <Text strong>{total.toFixed(1)}</Text>
                    </Tooltip>
                );
            },
            sorter: (a, b) => {
                const aTotal = a.monthlyHours.reduce((sum, m) => sum + m.hours, 0);
                const bTotal = b.monthlyHours.reduce((sum, m) => sum + m.hours, 0);
                return aTotal - bTotal;
            }
        }
    ], [filteredMonths]);

    const renderRouteKilometers = () => {
        if (!report?.routeSummary) return null;

        const handleExportCSV = () => {
            // Create CSV data that matches exactly what's shown in the table
            const csvData = filteredRouteData.map(route => {
                const row = {
                    'Route': route.routeNumber,
                    'Network ID': route.networkId || 'N/A'
                };
                
                // Add data for each filtered month (matching what's displayed)
                filteredMonths.forEach(month => {
                    const monthData = route.monthlyKilometers.find(m => m.month === month.month);
                    row[month.month] = monthData ? Math.round(monthData.kilometers) : 0;
                });
                
                // Add total (only for filtered months)
                const total = route.monthlyKilometers
                    .filter(m => filteredMonths.some(fm => fm.month === m.month))
                    .reduce((sum, m) => sum + m.kilometers, 0);
                row['Total'] = Math.round(total);
                
                return row;
            });
            
            // Create columns that match the table structure
            const csvColumns = [
                { title: 'Route', dataIndex: 'Route' },
                { title: 'Network ID', dataIndex: 'Network ID' },
                ...filteredMonths.map(month => ({ title: month.month, dataIndex: month.month })),
                { title: 'Total', dataIndex: 'Total' }
            ];
            
            const csvContent = convertToCSV(csvData, csvColumns);
            downloadCSV(csvContent, `route_kilometers_${dayjs().format('YYYY-MM-DD')}.csv`);
            message.success('Route Kilometers exported successfully');
        };

        return (
            <Card className="route-kilometers-card">
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Text type="secondary">
                                Planned Service Kilometers by Route and Month
                            </Text>
                            <Button 
                                icon={<DownloadOutlined />} 
                                onClick={handleExportCSV}
                                size="small"
                            >
                                Export CSV
                            </Button>
                        </Space>
                    </Col>
                    <Col span={24}>
                        <Table 
                            columns={routeKilometersColumns}
                            dataSource={filteredRouteData}
                            scroll={{ x: 'max-content' }}
                            pagination={{ 
                                pageSize: pageSize,
                                onChange: (page, size) => {
                                    console.log('Changed to page', page, 'with size', size);
                                },
                                onShowSizeChange: (current, size) => {
                                    console.log('Size changed to', size);
                                    setPageSize(size);
                                },
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100']
                            }}
                            className="route-table"
                        />
                    </Col>
                </Row>
            </Card>
        );
    };

    const renderRouteHours = () => {
        if (!report?.routeHoursSummary) return null;

        const handleExportCSV = () => {
            // Create CSV data that matches exactly what's shown in the table
            const csvData = filteredRouteHoursData.map(route => {
                const row = {
                    'Route': route.routeNumber,
                    'Network ID': route.networkId || 'N/A'
                };
                
                // Add data for each filtered month (matching what's displayed)
                filteredMonths.forEach(month => {
                    const monthData = route.monthlyHours.find(m => m.month === month.month);
                    row[month.month] = monthData ? monthData.hours.toFixed(1) : '0.0';
                });
                
                // Add total (only for filtered months)
                const total = route.monthlyHours
                    .filter(m => filteredMonths.some(fm => fm.month === m.month))
                    .reduce((sum, m) => sum + m.hours, 0);
                row['Total'] = total.toFixed(1);
                
                return row;
            });
            
            // Create columns that match the table structure
            const csvColumns = [
                { title: 'Route', dataIndex: 'Route' },
                { title: 'Network ID', dataIndex: 'Network ID' },
                ...filteredMonths.map(month => ({ title: month.month, dataIndex: month.month })),
                { title: 'Total', dataIndex: 'Total' }
            ];
            
            const csvContent = convertToCSV(csvData, csvColumns);
            downloadCSV(csvContent, `route_hours_${dayjs().format('YYYY-MM-DD')}.csv`);
            message.success('Route Hours exported successfully');
        };

        return (
            <Card className="route-hours-card">
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Text type="secondary">
                                Planned Service Hours by Route and Month
                            </Text>
                            <Button 
                                icon={<DownloadOutlined />} 
                                onClick={handleExportCSV}
                                size="small"
                            >
                                Export CSV
                            </Button>
                        </Space>
                    </Col>
                    <Col span={24}>
                        <Table 
                            columns={routeHoursColumns}
                            dataSource={filteredRouteHoursData}
                            scroll={{ x: 'max-content' }}
                            pagination={{ 
                                pageSize: pageSize,
                                onChange: (page, size) => {
                                    console.log('Changed to page', page, 'with size', size);
                                },
                                onShowSizeChange: (current, size) => {
                                    console.log('Size changed to', size);
                                    setPageSize(size);
                                },
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100']
                            }}
                            className="route-table"
                        />
                    </Col>
                </Row>
            </Card>
        );
    };

    const renderTripSummary = () => {
        if (!report?.tripSummary?.routeTripCounts) return null;

        const columns = [
            {
                title: 'Route',
                dataIndex: 'routeNumber',
                key: 'routeNumber',
                fixed: 'left',
                width: 120,
                render: (text) => (
                    <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                        {text}
                    </Tag>
                )
            },
            {
                title: 'Contract ID',
                dataIndex: 'networkId',
                key: 'networkId',
                fixed: 'left',
                width: 120,
                render: (text) => (
                    <Tag color="purple" style={{ fontSize: '14px', padding: '4px 8px' }}>
                        {text}
                    </Tag>
                )
            },
            ...filteredMonths.map(month => ({
                title: month.month,
                dataIndex: month.month,
                key: month.month,
                width: 120,
                render: (text) => {
                    return (
                        <Tooltip title={`${text?.toLocaleString() || 0} trips`}>
                            <span style={{ fontWeight: 500 }}>
                                {formatNumber(text || 0)}
                            </span>
                        </Tooltip>
                    );
                }
            }))
        ];

        const data = report.tripSummary.routeTripCounts.map(route => {
            const row = {
                key: route.routeId,
                routeNumber: route.routeNumber || route.routeId,
                networkId: route.networkId
            };
            
            Object.entries(route.monthlyTripCounts).forEach(([month, count]) => {
                row[month] = count;
            });
            
            return row;
        });

        // Filter data based on selected contract IDs
        const filteredData = selectedContractIds.length > 0
            ? data.filter(route => selectedContractIds.includes(route.networkId))
            : data;

        const handleExportCSV = () => {
            // Create CSV data that matches exactly what's shown in the table
            const csvData = filteredData.map(route => {
                const row = {
                    'Route': route.routeNumber,
                    'Contract ID': route.networkId || 'N/A'
                };
                
                // Add data for each filtered month (matching what's displayed)
                filteredMonths.forEach(month => {
                    row[month.month] = route[month.month] || 0;
                });
                
                return row;
            });
            
            // Create columns that match the table structure
            const csvColumns = [
                { title: 'Route', dataIndex: 'Route' },
                { title: 'Contract ID', dataIndex: 'Contract ID' },
                ...filteredMonths.map(month => ({ title: month.month, dataIndex: month.month }))
            ];
            
            const csvContent = convertToCSV(csvData, csvColumns);
            downloadCSV(csvContent, `trip_summary_${dayjs().format('YYYY-MM-DD')}.csv`);
            message.success('Trip Summary exported successfully');
        };

        return (
            <Card className="trip-summary-card">
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Text type="secondary">
                                Number of trips performed for each route by month
                            </Text>
                            <Button 
                                icon={<DownloadOutlined />} 
                                onClick={handleExportCSV}
                                size="small"
                            >
                                Export CSV
                            </Button>
                        </Space>
                    </Col>
                    <Col span={24}>
                        <Table 
                            columns={columns} 
                            dataSource={filteredData}
                            pagination={{ 
                                pageSize: pageSize,
                                onChange: (page, size) => {
                                    console.log('Changed to page', page, 'with size', size);
                                },
                                onShowSizeChange: (current, size) => {
                                    console.log('Size changed to', size);
                                    setPageSize(size);
                                },
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100']
                            }}
                            scroll={{ x: 'max-content' }}
                            className="summary-table"
                        />
                    </Col>
                </Row>
            </Card>
        );
    };

    const renderInsights = () => {
        if (!report?.insights) return null;

        return (
            <div className="insights-container">
                <Row gutter={[16, 16]}>
                    {report.insights.map((insight, index) => (
                        <Col xs={24} md={12} key={index}>
                            <Card 
                                title={
                                    <Space>
                                        <RiseOutlined style={{ color: '#1890ff' }} />
                                        <span>{insight.title}</span>
                                    </Space>
                                }
                                className="insight-card"
                            >
                                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                    {insight.description}
                                </Text>
                                <Row gutter={[8, 8]}>
                                    {insight.data.map((item, i) => (
                                        <Col span={24} key={i}>
                                            <Card size="small" bordered={false} style={{ background: '#fafafa' }}>
                                                <Row justify="space-between" align="middle">
                                                    <Col>
                                                        <Text strong>{item.metric}</Text>
                                                    </Col>
                                                    <Col>
                                                        <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
                                                            <Text strong style={{ 
                                                                color: item.value.includes('%') ? 
                                                                    getValueColor(parseFloat(item.value), 'percentage') : 
                                                                    getValueColor(0, 'default')
                                                            }}>
                                                                {item.value}
                                                            </Text>
                                                            {item.subValue && (
                                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                                    {item.subValue}
                                                                </Text>
                                                            )}
                                                        </Space>
                                                    </Col>
                                                </Row>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>
        );
    };

    const renderWarnings = () => {
        if (!report?.reportInfo?.dataWarnings || report.reportInfo.dataWarnings.length === 0) {
            return null;
        }

        return (
            <Alert
                message="Report Warnings"
                description={
                    <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                        {report.reportInfo.dataWarnings.map((warning, index) => (
                            <li key={index}>{warning.message}</li>
                        ))}
                    </ul>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
            />
        );
    };

    const renderReportInfo = () => {
        if (!report?.reportInfo) return null;

        const handlePrint = () => {
            // Generate summary stats content
            const summaryStats = report?.routeSummary && report.monthlyStats ? `
                <div class="report-section">
                    <h2>Summary Statistics</h2>
                    <div class="stats-grid">
                        <div class="stat-item">
                            <div class="stat-title">Total Routes</div>
                            <div class="stat-value">${(report.routeSummary || []).length}</div>
                        </div>
                        <div class="stat-item">
                            <div class="stat-title">Total Service Kilometers</div>
                            <div class="stat-value">${Math.round((report.monthlyStats || []).reduce(
                                (sum, month) => sum + Object.values(month?.routeKilometers || {})
                                    .reduce((s, r) => s + (r?.kilometers || 0), 0), 0
                            )).toLocaleString()}</div>
                        </div>
                        <div class="stat-item">
                            <div class="stat-title">Total Trips</div>
                            <div class="stat-value">${(report.monthlyStats || []).reduce(
                                (sum, month) => sum + (month?.totalTrips || 0), 0
                            ).toLocaleString()}</div>
                        </div>
                    </div>
                </div>
            ` : '';

            // Generate route kilometers table content
            const routeKilometers = report?.routeSummary ? `
                <div class="report-section">
                    <h2>Planned Service Kilometers (OR2)</h2>
                    <table>
                        <thead>
                            <tr>
                                <th>Route Number</th>
                                <th>Contract ID</th>
                                ${report.monthlyStats.map(month => `<th>${month.month}</th>`).join('')}
                            </tr>
                        </thead>
                        <tbody>
                            ${report.routeSummary.map(route => `
                                <tr>
                                    <td>${route.routeNumber}</td>
                                    <td>${route.networkId || '-'}</td>
                                    ${route.monthlyKilometers.map(mk => 
                                        `<td>${Math.round(mk.kilometers).toLocaleString()}</td>`
                                    ).join('')}
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            ` : '';

            // Generate deadrun kilometers table content
            const deadrunKilometers = report?.deadrunSummary ? `
                <div class="report-section">
                    <h2>Planned Deadrun Kilometers (OR2)</h2>
                    <table>
                        <thead>
                            <tr>
                                <th>Driver Shift</th>
                                <th>Dead-run Types</th>
                                <th>Total Dead-runs</th>
                                ${report.monthlyStats.map(month => `<th>${month.month}</th>`).join('')}
                            </tr>
                        </thead>
                        <tbody>
                            ${report.deadrunSummary.map(shift => `
                                <tr>
                                    <td>${shift.shiftName}</td>
                                    <td>${shift.deadrunTypes.join(', ')}</td>
                                    <td>${shift.totalDeadruns}</td>
                                    ${shift.monthlyKilometers.map(mk => 
                                        `<td>${Math.round(mk.kilometers).toLocaleString()}</td>`
                                    ).join('')}
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            ` : '';

            // Generate trip summary table content
            const tripSummary = report?.tripSummary?.routeTripCounts ? `
                <div class="report-section">
                    <h2>Planned Trips (OR3)</h2>
                    <table>
                        <thead>
                            <tr>
                                <th>Route</th>
                                <th>Contract ID</th>
                                ${report.monthlyStats.map(month => `<th>${month.month}</th>`).join('')}
                            </tr>
                        </thead>
                        <tbody>
                            ${report.tripSummary.routeTripCounts.map(route => `
                                <tr>
                                    <td>${route.routeNumber || route.routeId}</td>
                                    <td>${route.networkId || '-'}</td>
                                    ${report.monthlyStats.map(month => 
                                        `<td>${(route.monthlyTripCounts[month.month] || 0).toLocaleString()}</td>`
                                    ).join('')}
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            ` : '';

            // Generate insights content
            const insights = report?.insights ? `
                <div class="report-section">
                    <h2>Insights</h2>
                    <div class="insights-grid">
                        ${report.insights.map(insight => `
                            <div class="insight-item">
                                <h3>${insight.title}</h3>
                                <p>${insight.description}</p>
                                <div class="insight-metrics">
                                    ${insight.data.map(item => `
                                        <div class="metric-item">
                                            <div class="metric-title">${item.metric}</div>
                                            <div class="metric-value">${item.value}</div>
                                            ${item.subValue ? `<div class="metric-subvalue">${item.subValue}</div>` : ''}
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                        `).join('')}
                    </div>
                </div>
            ` : '';

            const printWindow = window.open('', '_blank');
            const printContent = `
                <html>
                    <head>
                        <title>OSR Report - ${dateRange[0].format('DD MMM YYYY')} to ${dateRange[1].format('DD MMM YYYY')}</title>
                        <style>
                            @media print {
                                body {
                                    font-family: Arial, sans-serif;
                                    margin: 20px;
                                    color: #000;
                                }
                                .report-section {
                                    page-break-after: always;
                                    margin-bottom: 20px;
                                }
                                .report-section:last-child {
                                    page-break-after: avoid;
                                }
                                .report-header {
                                    margin-bottom: 30px;
                                }
                                .report-title {
                                    font-size: 24px;
                                    font-weight: bold;
                                    margin-bottom: 10px;
                                }
                                .report-period {
                                    font-size: 18px;
                                    color: #333;
                                    margin-bottom: 15px;
                                }
                                .report-description {
                                    font-size: 14px;
                                    line-height: 1.5;
                                    margin-bottom: 15px;
                                }
                                .stats-grid {
                                    display: grid;
                                    grid-template-columns: repeat(3, 1fr);
                                    gap: 20px;
                                    margin-bottom: 30px;
                                }
                                .stat-item {
                                    padding: 15px;
                                    border: 1px solid #ddd;
                                    border-radius: 4px;
                                }
                                .stat-title {
                                    font-size: 14px;
                                    color: #666;
                                    margin-bottom: 5px;
                                }
                                .stat-value {
                                    font-size: 20px;
                                    font-weight: bold;
                                }
                                h2 {
                                    color: #333;
                                    margin: 20px 0;
                                }
                                table {
                                    width: 100%;
                                    border-collapse: collapse;
                                    margin-bottom: 20px;
                                    font-size: 12px;
                                }
                                th, td {
                                    border: 1px solid #ddd;
                                    padding: 8px;
                                    text-align: left;
                                }
                                th {
                                    background-color: #f5f5f5;
                                    font-weight: bold;
                                }
                                .timestamp {
                                    font-size: 12px;
                                    color: #666;
                                    margin-bottom: 20px;
                                }
                                .insights-grid {
                                    display: grid;
                                    grid-template-columns: repeat(2, 1fr);
                                    gap: 20px;
                                }
                                .insight-item {
                                    border: 1px solid #ddd;
                                    padding: 15px;
                                    border-radius: 4px;
                                }
                                .insight-item h3 {
                                    margin-top: 0;
                                    color: #1890ff;
                                }
                                .insight-metrics {
                                    margin-top: 15px;
                                }
                                .metric-item {
                                    margin-bottom: 10px;
                                }
                                .metric-title {
                                    font-weight: bold;
                                    margin-bottom: 5px;
                                }
                                .metric-value {
                                    font-size: 16px;
                                    color: #3f8600;
                                }
                                .metric-subvalue {
                                    font-size: 12px;
                                    color: #666;
                                }
                            }
                        </style>
                    </head>
                    <body>
                        <div class="timestamp">
                            Generated on: ${new Date().toLocaleString()}
                        </div>
                        <div class="report-section">
                            <div class="report-header">
                                <div class="report-title">OSR Report</div>
                                <div class="report-period">Report Period: ${dateRange[0].format('DD MMM YYYY')} to ${dateRange[1].format('DD MMM YYYY')}</div>
                                <div class="report-description">${report.reportInfo.description || ''}</div>
                            </div>
                        </div>
                        ${summaryStats}
                        ${routeKilometers}
                        ${deadrunKilometers}
                        ${tripSummary}
                        ${insights}
                    </body>
                </html>
            `;
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.print();
        };

        return (
            <Card className="report-info-card">
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text style={{ fontSize: '16px', lineHeight: '1.5' }}>
                                {report.reportInfo.description}
                            </Text>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text style={{ fontSize: '20px', fontWeight: '500' }}>
                                    <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                                    Report Period: {dateRange[0].format('DD MMM YYYY')} to {dateRange[1].format('DD MMM YYYY')}
                                </Text>
                                <Button 
                                    type="primary" 
                                    icon={<FileTextOutlined />}
                                    onClick={handlePrint}
                                >
                                    Print PDF
                                </Button>
                            </div>
                        </Space>
                    </Col>
                </Row>
            </Card>
        );
    };

    // Memoize summary stats since they're used in multiple tabs
    const summaryStats = useMemo(() => {
        if (!report?.routeSummary || !report.monthlyStats) return null;

        // Filter routes based on selected contract IDs
        const filteredRoutes = selectedContractIds.length > 0
            ? report.routeSummary.filter(route => selectedContractIds.includes(route.networkId))
            : report.routeSummary;

        // Calculate total kilometers for filtered routes
        const totalKilometers = Math.round(report.monthlyStats.reduce(
            (sum, month) => {
                const monthKilometers = Object.entries(month?.routeKilometers || {})
                    .reduce((s, [routeId, data]) => {
                        const route = report.routeSummary.find(r => r.routeNumber === routeId);
                        if (!route) return s;
                        if (selectedContractIds.length > 0 && !selectedContractIds.includes(route.networkId)) return s;
                        return s + (data?.kilometers || 0);
                    }, 0);
                return sum + monthKilometers;
            }, 0
        ));

        // Calculate total hours for filtered routes
        const totalHours = report.monthlyStats.reduce(
            (sum, month) => {
                const monthHours = Object.entries(month?.routeHours || {})
                    .reduce((s, [routeId, data]) => {
                        const route = report.routeHoursSummary?.find(r => r.routeNumber === routeId);
                        if (!route) return s;
                        if (selectedContractIds.length > 0 && !selectedContractIds.includes(route.networkId)) return s;
                        return s + (data?.hours || 0);
                    }, 0);
                return sum + monthHours;
            }, 0
        );

        // Calculate total trips for filtered routes
        const totalTrips = report.tripSummary?.routeTripCounts?.reduce((sum, route) => {
            // Skip if route should be filtered out
            if (selectedContractIds.length > 0 && !selectedContractIds.includes(route.networkId)) {
                return sum;
            }
            // Sum up all monthly trips for this route
            const routeTotal = Object.values(route.monthlyTripCounts || {}).reduce((s, count) => s + (count || 0), 0);
            return sum + routeTotal;
        }, 0) || 0;

        return {
            totalRoutes: filteredRoutes.length,
            totalKilometers,
            totalHours,
            totalTrips
        };
    }, [report?.routeSummary, report?.monthlyStats, report?.routeHoursSummary, report?.tripSummary, selectedContractIds]);

    const renderSummaryStats = () => {
        if (!summaryStats) return null;

        return (
            <Row gutter={[16, 16]}>
                <Col xs={24} md={6}>
                    <Card>
                        <Statistic
                            title={<Space><CarOutlined />Total Routes</Space>}
                            value={summaryStats.totalRoutes}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={6}>
                    <Card>
                        <Statistic
                            title={<Space><RiseOutlined />Total Service Kilometers</Space>}
                            value={summaryStats.totalKilometers}
                            suffix=""
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={6}>
                    <Card>
                        <Statistic
                            title={<Space><ClockCircleOutlined />Total Service Hours</Space>}
                            value={summaryStats.totalHours.toFixed(1)}
                            suffix=""
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={6}>
                    <Card>
                        <Statistic
                            title={<Space><BarChartOutlined />Total Trips</Space>}
                            value={summaryStats.totalTrips}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
            </Row>
        );
    };

    const renderRouteSummary = () => {
        if (!report?.routeSummary) return null;

        return (
            <Card className="route-summary-card">
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Text type="secondary">
                            Detailed breakdown of service kilometers by route and month
                        </Text>
                    </Col>
                    <Col span={24}>
                        <Table 
                            columns={report.routeSummary.map(route => ({
                                title: route.routeNumber,
                                dataIndex: route.routeNumber,
                                key: route.routeNumber,
                                width: 120,
                                render: (text) => {
                                    const value = route.monthlyKilometers.find(mk => mk.routeNumber === text)?.kilometers || 0;
                                    return (
                                        <Tooltip title={`${value.toLocaleString()} kilometers`}>
                                            {formatNumber(value)}
                                        </Tooltip>
                                    );
                                }
                            }))}
                            dataSource={report.routeSummary}
                            scroll={{ x: 'max-content' }}
                            pagination={{ 
                                pageSize: pageSize,
                                onChange: (page, size) => {
                                    console.log('Changed to page', page, 'with size', size);
                                },
                                onShowSizeChange: (current, size) => {
                                    console.log('Size changed to', size);
                                    setPageSize(size);
                                },
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100']
                            }}
                            className="route-table"
                        />
                    </Col>
                </Row>
            </Card>
        );
    };

    const renderOperationalMetrics = () => {
        if (!report?.operationalMetrics) return null;

        return (
            <Card className="operational-metrics-card">
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Text type="secondary">
                            Operational Metrics
                        </Text>
                    </Col>
                    <Col span={24}>
                        <Table 
                            columns={report.operationalMetrics.map(metric => ({
                                title: metric.title,
                                dataIndex: metric.title,
                                key: metric.title,
                                width: 120,
                                render: (text) => {
                                    const value = text ? Math.round(text) : 0;
                                    return (
                                        <Tooltip title={`${value.toLocaleString()}`}>
                                            {formatNumber(value)}
                                        </Tooltip>
                                    );
                                }
                            }))}
                            dataSource={report.operationalMetrics}
                            scroll={{ x: 'max-content' }}
                            pagination={{ 
                                pageSize: pageSize,
                                onChange: (page, size) => {
                                    console.log('Changed to page', page, 'with size', size);
                                },
                                onShowSizeChange: (current, size) => {
                                    console.log('Size changed to', size);
                                    setPageSize(size);
                                },
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100']
                            }}
                            className="metrics-table"
                        />
                    </Col>
                </Row>
            </Card>
        );
    };

    const renderDeadrunKilometers = () => {
        if (!report?.deadrunSummary) return null;

        const columns = [
            {
                title: 'Driver Shift',
                dataIndex: 'shiftName',
                key: 'shiftName',
                fixed: 'left',
                width: 120,
                render: (text) => (
                    <Tag color="purple" style={{ fontSize: '14px', padding: '4px 8px' }}>
                        {text}
                    </Tag>
                )
            },
            {
                title: 'Dead-run Types',
                dataIndex: 'deadrunTypes',
                key: 'deadrunTypes',
                width: 150,
                render: (types) => (
                    <Space>
                        {types.map((type, index) => (
                            <Tag key={index} color="blue">
                                {type}
                            </Tag>
                        ))}
                    </Space>
                )
            },
            {
                title: 'Total Dead-runs',
                dataIndex: 'totalDeadruns',
                key: 'totalDeadruns',
                width: 120,
                render: (count) => (
                    <Text strong>{count}</Text>
                )
            },
            ...filteredMonths.map(month => ({
                title: month.month,
                dataIndex: month.month,
                key: month.month,
                width: 120,
                render: (text) => {
                    const value = text ? Math.round(text) : 0;
                    return (
                        <Tooltip title={`${value.toLocaleString()} kilometers`}>
                            {formatNumber(value)}
                        </Tooltip>
                    );
                }
            }))
        ];

        const data = report.deadrunSummary.map(shift => {
            const row = { 
                key: shift.shiftId,
                shiftId: shift.shiftId,
                shiftName: shift.shiftName,
                vehicleShift: shift.vehicleShift,
                deadrunTypes: shift.deadrunTypes,
                totalDeadruns: shift.totalDeadruns
            };
            shift.monthlyKilometers.forEach(mk => {
                row[mk.month] = mk.kilometers;
            });
            return row;
        });

        const handleExportCSV = () => {
            // Create CSV data that matches exactly what's shown in the table
            const csvData = data.map(shift => {
                const row = {
                    'Driver Shift': shift.shiftName,
                    'Dead-run Types': shift.deadrunTypes.join(', '),
                    'Total Dead-runs': shift.totalDeadruns
                };
                
                // Add data for each filtered month (matching what's displayed)
                filteredMonths.forEach(month => {
                    const value = shift[month.month] ? Math.round(shift[month.month]) : 0;
                    row[month.month] = value;
                });
                
                return row;
            });
            
            // Create columns that match the table structure
            const csvColumns = [
                { title: 'Driver Shift', dataIndex: 'Driver Shift' },
                { title: 'Dead-run Types', dataIndex: 'Dead-run Types' },
                { title: 'Total Dead-runs', dataIndex: 'Total Dead-runs' },
                ...filteredMonths.map(month => ({ title: month.month, dataIndex: month.month }))
            ];
            
            const csvContent = convertToCSV(csvData, csvColumns);
            downloadCSV(csvContent, `deadrun_kilometers_${dayjs().format('YYYY-MM-DD')}.csv`);
            message.success('Deadrun Kilometers exported successfully');
        };

        return (
            <Card className="deadrun-kilometers-card">
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <div>
                                <Space align="center">
                                    <CarOutlined style={{ fontSize: '20px', color: '#722ed1' }} />
                                    <Text strong>Dead-run Kilometers by Driver Shift</Text>
                                </Space>
                                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                                    Detailed breakdown of dead-run kilometers by driver shift and month. Shows the types of dead-runs (pull-in, pull-out, etc.) and total number of dead-runs per shift.
                                </Text>
                            </div>
                            <Button 
                                icon={<DownloadOutlined />} 
                                onClick={handleExportCSV}
                                size="small"
                            >
                                Export CSV
                            </Button>
                        </Space>
                    </Col>
                    <Col span={24}>
                        <Table 
                            columns={columns} 
                            dataSource={data}
                            pagination={{ 
                                pageSize: pageSize,
                                onChange: (page, size) => setPageSize(size),
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100']
                            }}
                            scroll={{ x: 'max-content' }}
                            className="deadrun-table"
                        />
                    </Col>
                </Row>
            </Card>
        );
    };

    return (
        <div style={{ padding: 24 }} className="osr-report">
            <Row gutter={[0, 24]}>
                <Col span={24}>
                    <Title level={4}>
                        <Space>
                            <CarOutlined />
                            Operational Reports
                        </Space>
                    </Title>
                </Col>

                <Col span={24}>
                    <Card>
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <Row gutter={16} align="middle">
                                <Col xs={24} sm={12}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <OTRDateRangePicker
                                            value={dateRange}
                                            onChange={setDateRange}
                                            displayFormat="DD MMM YYYY"
                                        />
                                    </Space>
                                </Col>
                                <Col xs={24} sm={8}>
                                    {report && (
                                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                            <Text type="secondary">Filter by Contract ID</Text>
                                            <Select
                                                mode="multiple"
                                                style={{ width: '100%' }}
                                                placeholder="Select Contract IDs"
                                                value={selectedContractIds}
                                                onChange={setSelectedContractIds}
                                                allowClear
                                                size="large"
                                            >
                                                {[...new Set(report.routeSummary.map(route => route.networkId))].filter(Boolean).map(id => (
                                                    <Select.Option key={id} value={id}>
                                                        {id}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Space>
                                    )}
                                </Col>
                                <Col xs={24} sm={4} style={{ textAlign: 'right' }}>
                                    {gtfsFile && dateRange && (
                                        <Button
                                            type="primary"
                                            onClick={handleGenerateReport}
                                            loading={loading}
                                            size="large"
                                            icon={<BarChartOutlined />}
                                        >
                                            Generate
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        </Space>
                    </Card>
                </Col>

                {loading && (
                    <Col span={24} style={{ textAlign: 'center' }}>
                        <Spin size="large" tip="Processing GTFS data..." />
                    </Col>
                )}

                {report && (
                    <>
                        <Col span={24}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                {renderWarnings()}
                                {renderReportInfo()}
                            </Space>
                        </Col>
                        
                        <Col span={24}>
                            <Card>
                                <Tabs 
                                    activeKey={activeTab}
                                    onChange={setActiveTab}
                                    type="card"
                                >
                                    <Tabs.TabPane
                                        key="1"
                                        tab={
                                            <Space>
                                                <CarOutlined />
                                                Planned Service Kms (OR2)
                                            </Space>
                                        }
                                    >
                                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                            {renderSummaryStats()}
                                            {renderRouteKilometers()}
                                        </Space>
                                    </Tabs.TabPane>
                                    <Tabs.TabPane
                                        key="2"
                                        tab={
                                            <Space>
                                                <ClockCircleOutlined />
                                                Planned Service Hours (OR2)
                                            </Space>
                                        }
                                    >
                                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                            {renderSummaryStats()}
                                            {renderRouteHours()}
                                        </Space>
                                    </Tabs.TabPane>
                                    <Tabs.TabPane
                                        key="3"
                                        tab={
                                            <Space>
                                                <CarOutlined style={{ color: '#722ed1' }} />
                                                Planned Deadrun Kms (OR2)
                                            </Space>
                                        }
                                    >
                                        {renderDeadrunKilometers()}
                                    </Tabs.TabPane>
                                    <Tabs.TabPane
                                        key="4"
                                        tab={
                                            <Space>
                                                <BarChartOutlined />
                                                Planned Trips (OR3)
                                            </Space>
                                        }
                                    >
                                        {renderTripSummary()}
                                    </Tabs.TabPane>
                                    <Tabs.TabPane
                                        key="5"
                                        tab={
                                            <Space>
                                                <RiseOutlined />
                                                Insights
                                            </Space>
                                        }
                                    >
                                        {renderInsights()}
                                    </Tabs.TabPane>
                                </Tabs>
                            </Card>
                        </Col>
                    </>
                )}
            </Row>

            <style jsx global>{`
                .osr-report .ant-card {
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
                }
                
                .osr-report .route-table .ant-table-thead > tr > th,
                .osr-report .deadrun-table .ant-table-thead > tr > th {
                    background: #fafafa;
                    font-weight: 600;
                }

                .osr-report .insight-card {
                    height: 100%;
                    border-radius: 8px;
                }

                .osr-report .insight-card .ant-card-head {
                    border-bottom: none;
                    padding-bottom: 0;
                }

                .osr-report .summary-table .ant-table-thead > tr > th {
                    background: #fafafa;
                    font-weight: 600;
                }

                .osr-report .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
                    border-radius: 6px 6px 0 0;
                    padding: 8px 16px;
                }

                .osr-report .ant-statistic-title {
                    font-size: 16px;
                    margin-bottom: 8px;
                }
            `}</style>
        </div>
    );
} 